<template>
  <div class="for-transition w-100 flex flex-column gap-medium">
    <!-- Phone and Email -->
    <div>
      <h4 class="mn-b-small">{{ !(core.state?.options?.auth?.authMethodsExclude || []).includes('phone') ? t('auth.profileEdit.account.contactsTitle') : t('auth.profileEdit.account.emailTitle') }}</h4>
      <div class="card">
        <!-- Phone -->
        <div v-if="!(core.state?.options?.auth?.authMethodsExclude || []).includes('phone')" class="mn-b-medium">
          <Field
            v-if="users.state.current.phone && !isEditingPhone"
            v-model:field="users.state.current.phone"
            :label="t('auth.profileEdit.account.phone')"
            class="mn-b-thin input input--l"
            disabled
          />
          <FieldPhone
            v-else
            @change="(val) => newPhone = val"
            :dropdownOptions="{
              showDialCodeInSelection: true,
              showFlags: true,
              showDialCodeInList: true
            }"
            :validCharactersOnly="true"
            mode="national"
            :inputOptions="{ placeholder: t('auth.profileEdit.account.phonePlaceholder'), type: 'tel' }"
            class="input input--l mn-b-thin"
          />
          <Button
            v-if="users.state.current.phone && !isEditingPhone"
            :submit="() => isEditingPhone = true"
            class="button--card button--m w-100"
          >
            {{ t('auth.profileEdit.account.changePhone') }}
          </Button>
          <Button
            v-else-if="!users.state.current.phone"
            :submit="() => savePhone()"
            class="button--accent button--m w-100"
          >
            {{ t('auth.profileEdit.account.savePhone') }}
          </Button>
          <div v-else class="flex gap-small">
            <Button
              :submit="() => savePhone()"
              class="button--accent button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.savePhone') }}
            </Button>
            <Button
              :submit="() => { isEditingPhone = false; newPhone = '' }"
              class="button--card button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.cancel') }}
            </Button>
          </div>
        </div>

        <!-- Email -->
        <div>
          <Field
            v-if="users.state.current.email && !isEditingEmail"
            v-model:field="users.state.current.email"
            :label="t('auth.profileEdit.account.email')"
            class="mn-b-thin input input--l"
            disabled
          />
          <Field
            v-else
            v-model:field="newEmail"
            :label="t('auth.profileEdit.account.email')"
            :placeholder="t('auth.profileEdit.account.emailPlaceholder')"
            class="mn-b-thin input input--l"
          />
          <Button
            v-if="users.state.current.email && !isEditingEmail"
            :submit="() => isEditingEmail = true"
            class="button--card button--m w-100"
          >
            {{ t('auth.profileEdit.account.changeEmail') }}
          </Button>
          <Button
            v-else-if="!users.state.current.email"
            :submit="() => saveEmail()"
            class="button--accent button--m w-100"
          >
            {{ t('auth.profileEdit.account.saveEmail') }}
          </Button>
          <div v-else class="flex gap-small">
            <Button
              :submit="() => saveEmail()"
              class="button--accent button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.saveEmail') }}
            </Button>
            <Button
              :submit="() => { isEditingEmail = false; newEmail = '' }"
              class="button--card button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.cancel') }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings -->
    <div>
      <h4 class="mn-b-small">{{ t('auth.profileEdit.account.settingsTitle') }}</h4>
      <!-- Строка-переключатель — card, как строки каналов в «Уведомлениях»:
           текст слева (w-100 распирает строку), свитч справа. Тема применяется
           сразу — отдельного «Сохранить» на этой странице нет. -->
      <label class="card card--compact flex flex-v-center flex-between cursor-pointer">
        <span class="w-100">{{ t('auth.profileEdit.account.darkmode') }}</span>
        <Checkbox
          mode="switch"
          :radio="ds.theme.mode === 'dark'"
          @update:radio="() => ds.theme.toggle()"
          class="flex-shrink-0"
        />
      </label>
    </div>

    <!-- Account Actions -->
    <div>
      <h4 class="mn-b-small">{{ t('auth.profileEdit.account.accountTitle') }}</h4>
      <div class="flex flex-column gap-small">
        <Button
          :submit="logout"
          class="button--card button--l w-100"
        >
          {{ t('auth.profileEdit.account.logout') }}
        </Button>

        <Button
          :submit="onSubmitDelete"
          :callback="() => router.push({ name: 'Sign In' })"
          class="button--negative button--l w-100"
        >
          {{ t('auth.profileEdit.account.deleteAccount') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import Checkbox from '@martyrs/src/design-system/components/Checkbox/Checkbox.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue'
import { ds } from '@martyrs/src/design-system/index.js'
import Field from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'

import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import * as auth from '@martyrs/src/modules/auth/views/store/auth.js'
import * as users from '@martyrs/src/modules/auth/views/store/users.js'
import { useStore, setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import { withOtp } from '@martyrs/src/modules/auth/views/plugins/otp.plugin.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'

const store = useStore()
const core = store.core
const router = useRouter()
const { t } = useI18n()

const isEditingPhone = ref(false)
const isEditingEmail = ref(false)
const newPhone = ref('')
const newEmail = ref('')

defineProps({
  userData: {
    type: Object,
    required: true
  }
})

async function savePhone() {
  if (!newPhone.value) return

  try {
    const result = await withOtp(
      (data) => $axios.put(`/api/users/${users.state.current._id}/phone`, data),
      { phone: newPhone.value }
    )

    if (result.data) {
      users.state.current.phone = result.data.phone
    }

    isEditingPhone.value = false
    newPhone.value = ''
  } catch (error) {
    console.log('savePhone catch:', error)
    setError(error)
  }
}

async function saveEmail() {
  if (!newEmail.value) return

  try {
    const result = await withOtp(
      (data) => $axios.put(`/api/users/${users.state.current._id}/email`, data),
      { email: newEmail.value }
    )

    if (result.data) {
      users.state.current.email = result.data.email
    }

    isEditingEmail.value = false
    newEmail.value = ''
  } catch (error) {
    setError(error)
  }
}

async function onSubmitDelete() {
  const confirmation = window.confirm(t('auth.profileEdit.account.deleteConfirm'))

  if (!confirmation) return

  users.state.current.status = 'removed'
  await users.actions.update(users.state.current)
  auth.actions.logout()
}

function logout() {
  auth.actions.logout()
  router.push({ name: 'Sign In' })
}
</script>
